import { ChatMessage } from "@/configs/@types/socket";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { registerReset } from "@/store/resetStore";

type Chat = {
  messages: ChatMessage[];
  status: 'active' | 'inactive';
  cname?: string;
  customerId?: string;
  unreadCount?: number;
  historyLoaded?: boolean;
  customerLeftChat?: boolean;
  lastActivityTime?: number;
  queueId?: string | number;
  channel?: "WEBBOT" | "WHATSAPP";
};

type ChatState = {
  chats: Record<string, Chat>;
  activeChatId?: string;
  addMessage: (chatId: string, message: ChatMessage) => void;
  setStatus: (chatId: string, status: 'active' | 'inactive') => void;
  setCname: (chatId: string, cname: string) => void;
  setCustomerId: (chatId: string, customerId: string) => void;
  setQueueId: (chatId: string, queueId: string | number) => void;
  setChannel: (chatId: string, channel: "WEBBOT" | "WHATSAPP") => void;
  setActiveChat: (chatId: string) => void;
  markChatRead: (chatId: string) => void;
  markCustomerLeftChat: (chatId: string) => void;
  resetChat: (chatId: string) => void;
  loadChatHistory: (chatId: string, historyMessages: ChatMessage[]) => void;
  clearAllChats: () => void;
  touchChat: (chatId: string) => void;
};

const initialState = {
  chats: {},
};

export const useChatStore = create<ChatState>()(
  devtools(
    persist(
      (set) => {
        // REGISTER RESET
        registerReset(() => set(initialState));

        return {
          ...initialState,

          addMessage: (chatId, message) =>
            set(
              (state) => {
                const existing = state.chats[chatId] || { messages: [], status: 'active', unreadCount: 0 };

                console.log('addMessage called:', {
                  chatId,
                  text: message.text?.substring(0, 40),
                  sender: message.sender,
                  isMe: message.isMe,
                  messageId: message.id,
                  currentCount: existing.messages?.length || 0
                });

                // Only skip if exact same message ID (true WebSocket duplicate)
                const hasSameId = existing.messages.some(m => m.id === message.id);
                if (hasSameId) {
                  console.log('Same ID detected, SKIPPING duplicate');
                  return state;
                }

                // Add all messages - don't filter out send+echo or variations
                console.log('Adding new message');

                const isIncomingUser = (message.sender === 'user') && message.isMe !== true;
                const isActive = state.activeChatId === chatId;
                const shouldIncrement = isIncomingUser && !isActive;
                const nextUnread = shouldIncrement ? (existing.unreadCount || 0) + 1 : (existing.unreadCount || 0);

                const newState = {
                  chats: {
                    ...state.chats,
                    [chatId]: {
                      ...existing,
                      status: existing.status || 'active',
                      messages: [...(existing.messages || []), message],
                      unreadCount: nextUnread,
                      lastActivityTime: Date.now(),
                    },
                  },
                };

                console.log('New count:', newState.chats[chatId].messages.length);
                return newState;
              },
              false,
              {
                type: "chat/addMessage",
                chatId,
              }
            ),

          setStatus: (chatId, status) =>
            set(
              (state) => ({
                chats: {
                  ...state.chats,
                  [chatId]: {
                    ...state.chats[chatId],
                    status,
                  },
                },
              }),
              false,
              {
                type: "chat/setStatus",
                chatId,
                status,
              }
            ),

          setCname: (chatId, cname) =>
            set(
              (state) => {
                console.log('setCname called - chatId:', chatId, 'cname:', cname);
                console.log('Current chat state before setCname:', state.chats[chatId]);
                const newState = {
                  chats: {
                    ...state.chats,
                    [chatId]: {
                      ...state.chats[chatId],
                      messages: state.chats[chatId]?.messages || [],
                      status: state.chats[chatId]?.status || 'active',
                      cname,
                    },
                  },
                };
                console.log('New chat state after setCname:', newState.chats[chatId]);
                return newState;
              },
              false,
              {
                type: "chat/setCname",
                chatId,
                cname,
              }
            ),

          setCustomerId: (chatId, customerId) =>
            set(
              (state) => {
                console.log('setCustomerId called - chatId:', chatId, 'customerId:', customerId);
                const newState = {
                  chats: {
                    ...state.chats,
                    [chatId]: {
                      ...state.chats[chatId],
                      messages: state.chats[chatId]?.messages || [],
                      status: state.chats[chatId]?.status || 'active',
                      customerId,
                    },
                  },
                };
                console.log('New chat state after setCustomerId:', newState.chats[chatId]);
                return newState;
              },
              false,
              {
                type: "chat/setCustomerId",
                chatId,
                customerId,
              }
            ),

          setQueueId: (chatId, queueId) =>
            set(
              (state) => ({
                chats: {
                  ...state.chats,
                  [chatId]: {
                    ...state.chats[chatId],
                    messages: state.chats[chatId]?.messages || [],
                    status: state.chats[chatId]?.status || 'inactive',
                    queueId,
                  },
                },
              }),
              false,
              {
                type: "chat/setQueueId",
                chatId,
                queueId,
              }
            ),

          setChannel: (chatId, channel) =>
            set(
              (state) => ({
                chats: {
                  ...state.chats,
                  [chatId]: {
                    ...state.chats[chatId],
                    messages: state.chats[chatId]?.messages || [],
                    status: state.chats[chatId]?.status || 'active',
                    channel,
                  },
                },
              }),
              false,
              {
                type: "chat/setChannel",
                chatId,
                channel,
              }
            ),

          setActiveChat: (chatId) =>
            set(
              (state) => ({
                activeChatId: chatId,
                chats: {
                  ...state.chats,
                  [chatId]: {
                    ...state.chats[chatId],
                    messages: state.chats[chatId]?.messages || [],
                    status: state.chats[chatId]?.status || 'active',
                    unreadCount: 0,
                  },
                },
              }),
              false,
              {
                type: 'chat/setActiveChat',
                chatId,
              }
            ),

          markChatRead: (chatId) =>
            set(
              (state) => ({
                chats: {
                  ...state.chats,
                  [chatId]: {
                    ...state.chats[chatId],
                    messages: state.chats[chatId]?.messages || [],
                    status: state.chats[chatId]?.status || 'active',
                    unreadCount: 0,
                  },
                },
              }),
              false,
              {
                type: 'chat/markChatRead',
                chatId,
              }
            ),

          markCustomerLeftChat: (chatId) =>
            set(
              (state) => ({
                chats: {
                  ...state.chats,
                  [chatId]: {
                    ...state.chats[chatId],
                    messages: state.chats[chatId]?.messages || [],
                    status: state.chats[chatId]?.status || 'active',
                    customerLeftChat: true,
                  },
                },
              }),
              false,
              {
                type: 'chat/markCustomerLeftChat',
                chatId,
              }
            ),

          resetChat: (chatId) =>
            set(
              (state) => {
                const copy = { ...state.chats };
                delete copy[chatId];
                return { chats: copy };
              },
              false,
              {
                type: "chat/resetChat",
                chatId,
              }
            ),

          clearAllChats: () =>
            set(
              () => initialState,
              false,
              {
                type: "chat/clearAllChats",
              }
            ),

          touchChat: (chatId) =>
            set(
              (state) => ({
                chats: {
                  ...state.chats,
                  [chatId]: {
                    ...state.chats[chatId],
                    messages: state.chats[chatId]?.messages || [],
                    status: state.chats[chatId]?.status || 'active',
                    lastActivityTime: Date.now(),
                  },
                },
              }),
              false,
              {
                type: 'chat/touchChat',
                chatId,
              }
            ),

          loadChatHistory: (chatId, historyMessages) =>
            set(
              (state) => {
                const existing = state.chats[chatId] || { messages: [], status: 'active', unreadCount: 0 };

                // Merge history with existing messages, avoiding duplicates
                const mergedMessages = [...historyMessages];

                existing.messages.forEach(existingMsg => {
                  const isDuplicate = historyMessages.some(histMsg => {
                    const textMatch = histMsg.text === existingMsg.text;
                    const senderMatch = histMsg.sender === existingMsg.sender;
                    const timeMatch = Math.abs((histMsg.timestamp || 0) - (existingMsg.timestamp || 0)) < 2000;
                    return textMatch && senderMatch && timeMatch;
                  });

                  if (!isDuplicate) {
                    mergedMessages.push(existingMsg);
                  }
                });

                // Sort by timestamp
                mergedMessages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

                console.log('Loading chat history:', {
                  chatId,
                  historyCount: historyMessages.length,
                  existingCount: existing.messages.length,
                  mergedCount: mergedMessages.length
                });

                return {
                  chats: {
                    ...state.chats,
                    [chatId]: {
                      ...existing,
                      messages: mergedMessages,
                      historyLoaded: true,
                    },
                  },
                };
              },
              false,
              {
                type: "chat/loadChatHistory",
                chatId,
              }
            ),
        };
      },
      {
        name: "chat-storage",
      },
    ),
    {
      name: "chat-store",
    }
  )
);

